import { SearchOutlined } from "@ant-design/icons";
import React, { useRef, useState, useEffect } from "react";
import {
  Button,
  FormInstance,
  Input,
  Space,
  Table,
  TableProps,
  Form,
} from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";
import { PriceType } from "models";
import {
  EditableOrderPrestataireCell,
  EditableOrderPrestataireRow,
  OrderPrestataireCellRef,
  OrderPrestataireLine,
} from "./OrderPrestataireCellTable";
import type { InputRef } from "antd";
import Highlighter from "react-highlight-words";
import { UrbanCenterWithZone } from "types";
import { SelectedProductsAndSellingPrices } from "hooks/catalog-price-zone-service-provider";

interface PrestataireTableProps {
  form: FormInstance;
  dataSource: OrderPrestataireLine[];
  setDataSource: React.Dispatch<React.SetStateAction<OrderPrestataireLine[]>>;
  setLoadingFilter: (value: boolean) => void;
  isLoading: boolean;
  priceTypes: PriceType[];
  selectedRowKey: number | null;
  setSelectedRowKey: React.Dispatch<React.SetStateAction<number | null>>;
  serviceProviderZoneIds: UrbanCenterWithZone[];
  onSelectRow: (row: OrderPrestataireLine) => void;
  effectSPIdsLoading: number[];
  setEffectSPIdsLoading: React.Dispatch<React.SetStateAction<number[]>>;
  selectedProductLines: {
    priceFamilyId?: number;
    productId?: number;
    quantity?: number;
    productNameForClient?: string;
  }[];
  onAssociateProducts?: (
    selectedProducts: {
      uuid: string;
      productId: number;
      productName: string;
      quantity: number;
    }[]
  ) => void;
  selectedProductsFromOrderClient: SelectedProductsAndSellingPrices[];
}

const OrderPrestataireTable = (props: PrestataireTableProps) => {
  const {
    form,
    dataSource,
    setDataSource,
    setLoadingFilter,
    isLoading,
    priceTypes,
    serviceProviderZoneIds,
    onSelectRow,
    effectSPIdsLoading,
    setEffectSPIdsLoading,
    selectedProductLines,
    onAssociateProducts,
    selectedProductsFromOrderClient,
  } = props;
  const orderPrestataireCellRef = useRef<{
    [key: string]: OrderPrestataireCellRef | null;
  }>({});
  const [searchText, setSearchText] = useState("");
  const [isLoadingFilter, setIsLoadingFilter] = useState<boolean>(false);
  const [searchedColumn, setSearchedColumn] = useState<
    keyof OrderPrestataireLine | ""
  >("");
  const [filteredDataSource, setFilteredDataSource] =
    useState<OrderPrestataireLine[]>(dataSource);
  const searchInput = useRef<InputRef>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const formValuesWatch = Form.useWatch([], form);

  useEffect(() => {
    setIsLoadingFilter(true);
    // Filter dataSource item is Hide
    const filtered = dataSource.filter((record) => !record.isHide);
    setFilteredDataSource(filtered);
    setIsLoadingFilter(false);
    setLoadingFilter(false);
  }, [dataSource]);
  useEffect(() => {
    dataSource.forEach((item) => {
      item.products.forEach((product) => {
        form.setFieldsValue({
          lineItems: {
            [`${item.serviceProviderId}`]: {
              products: {
                [`${product.uuid}-${product.productId}`]: {
                  quantity: product.quantity ?? 1,
                },
              },
            },
          },
        });
      });
    });
  }, [dataSource, form]);
  // Scroll to top when dataSource changes
  useEffect(() => {
    if (dataSource.length > 0 && dataSource[0].isNew && tableRef.current) {
      const scrollContainer = tableRef.current.querySelector(".ant-table-body");
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
      // Remove isNew after animation duration (3000ms)
      const timer = setTimeout(() => {
        setDataSource((prev) =>
          prev.map((item) => (item.isNew ? { ...item, isNew: false } : item))
        );
      }, 3000); // Matches the animation duration
      return () => clearTimeout(timer); // Cleanup timer
    }
  }, [dataSource]);

  // Calculate total price for a service provider
  const allPricesValid = (serviceProviderId: number) => {
    const lineItem = formValuesWatch?.["lineItems"]?.[`${serviceProviderId}`];
    const selectedSP = dataSource.find(
      (sp) => sp.serviceProviderId === serviceProviderId
    );

    if (!selectedSP?.products || !lineItem) return false;

    return selectedSP.products.every((p) => {
      const prixUnitaire =
        lineItem?.["products"]?.[`${p.uuid + "-" + p.productId}`]?.[
          "prixUnitaire"
        ];
      const price = parseFloat(prixUnitaire || "0");
      return price > 0;
    });
  };

  const updateDataSource = (
    data: OrderPrestataireLine,
    serviceProviderId?: number
  ) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.serviceProviderId === serviceProviderId
          ? { ...item, ...data }
          : item
      )
    );
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: keyof OrderPrestataireLine | ""
  ) => {
    confirm();
    setSearchText(selectedKeys[0] || "");
    setSearchedColumn(dataIndex);
    if (!selectedKeys[0] || !dataIndex) {
      setFilteredDataSource(dataSource);
    } else {
      const normalizeString = (str: string) =>
        str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
      setFilteredDataSource(
        dataSource.filter((record) =>
          record[dataIndex]
            ? normalizeString(String(record[dataIndex])).includes(
                normalizeString(selectedKeys[0])
              )
            : false
        )
      );
    }
  };

  const handleReset = (
    clearFilters?: () => void,
    confirm?: FilterDropdownProps["confirm"]
  ) => {
    if (clearFilters) {
      clearFilters();
    }
    setSearchText("");
    setSearchedColumn("");
    setFilteredDataSource(dataSource);
    if (confirm) {
      handleSearch([], confirm, "");
    }
  };

  const getColumnSearchProps = (dataIndex: keyof OrderPrestataireLine) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Recherche`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
          >
            Recherche
          </Button>
          <Button
            className="btn-reset"
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
          >
            Réinitialiser
          </Button>
          <Button
            className="btn-filter"
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0] || "");
              setSearchedColumn(dataIndex);
            }}
          >
            Filtre
          </Button>
          <Button
            className="btn-close"
            type="link"
            size="small"
            onClick={() => close()}
          >
            Fermer
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#A6C84D" : undefined }} />
    ),
    onFilter: (value: string, record: OrderPrestataireLine) => {
      if (!record[dataIndex]) return false;
      const normalizeString = (str: string) =>
        str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
      return normalizeString(String(record[dataIndex])).includes(
        normalizeString(value)
      );
    },
    onFilterDropdownOpenChange: (open: boolean) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text: string, record: OrderPrestataireLine) => {
      if (record.creationType === "header") {
        return {
          props: {
            colSpan: 6,
          },
        };
      }
      return searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      );
    },
  });

  const defaultColumns = [
    {
      title: () => (
        <span
          style={{
            color:
              searchedColumn === "serviceProviderName" && searchText
                ? "#A6C84D"
                : "inherit",
          }}
        >
          Prestataires
        </span>
      ),
      dataIndex: "CatalogPriceLineServiceProvider",
      editable: true,
      width: 220,
      onCell: (record: OrderPrestataireLine) => ({
        rowSpan: record.rowSpan,
      }),
    },
    {
      title: "Produits",
      dataIndex: "CatalogPriceLineProducts",
      editable: true,
      width: 500,
      onCell: (record: OrderPrestataireLine) => ({
        rowSpan: record.rowSpan,
      }),
      render: (_: string, record: OrderPrestataireLine) => {
        if (record.creationType === "header") {
          return {
            props: {
              colSpan: 6,
            },
          };
        }
      },
    },
    {
      title: "Prix prestataires",
      dataIndex: "priceValue",
      editable: true,
      width: 150,
      render: (_: string, record: OrderPrestataireLine) => {
        if (record.creationType === "header") {
          return {
            props: {
              colSpan: 6,
            },
          };
        }
      },
    },
    {
      title: 'Prix de vente',
      dataIndex: 'clientOrderPrice',
      editable: true,
      width: 150,
      render: (_: string, record: OrderPrestataireLine) => {
        if (record.creationType === 'header') {
          return {
            props: {
              colSpan: 6,
            },
          };
        }
      },
    },
    {
      title: "Marge",
      dataIndex: "marge",
      editable: true,
      width: 60,
      render: (_: string, record: OrderPrestataireLine) => {
        if (record.creationType === "header") {
          return {
            props: {
              colSpan: 6,
            },
          };
        }
      },
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      editable: true,
      width: 80,
      minWidth: 70,
      className: "no-wrap",
      render: (_: string, record: OrderPrestataireLine) => {
        if (record.creationType === "header") {
          return {
            props: {
              colSpan: 6,
            },
          };
        }
      },
    },
    {
      dataIndex: "rowSelect",
      width: 10,
      onCell: (record: OrderPrestataireLine) => ({
        rowSpan: record.rowSpan,
      }),
      render: (_: unknown, record: OrderPrestataireLine) => {
        const isValid = allPricesValid(record.serviceProviderId);
        return (
          <Button
            className="ant-btn-primary"
            style={{
              backgroundColor: "#A6C84D",
              borderColor: "#A6C84D",
              boxShadow: "none",
            }}
            onClick={() => onSelectRow(record)}
            disabled={!isValid}
          >
            Choisir
          </Button>
        );
      },
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: OrderPrestataireLine) => ({
        form,
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        priceTypes,
        updateDataSource,
        serviceProviderZoneIds,
        effectSPIdsLoading,
        setEffectSPIdsLoading,
        selectedProductLines,
        onAssociateProducts,
        selectedProductsFromOrderClient,
        ref: (el: OrderPrestataireCellRef) => {
          if (orderPrestataireCellRef.current) {
            orderPrestataireCellRef.current[record.serviceProviderId] = el;
          }
        },
      }),
    };
  }) as TableProps<OrderPrestataireLine>["columns"];

  return (
    <section className="section quotation" style={{ width: "100%" }}>
      <Table
        bordered
        className="catalog-price-table"
        loading={isLoading || isLoadingFilter}
        components={{
          body: {
            row: EditableOrderPrestataireRow,
            cell: EditableOrderPrestataireCell,
          },
        }}
        columns={columns}
        dataSource={filteredDataSource}
        pagination={false}
        scroll={{ x: 1200, y: 550 }}
        tableLayout="auto"
        rowClassName={(record: OrderPrestataireLine) =>
          record.isNew ? "new-service-provider-row" : ""
        }
        ref={tableRef}
        rowKey={(record) => record.serviceProviderId.toString()}
      />
    </section>
  );
};

export default OrderPrestataireTable;
